import { useQuery } from '@tanstack/react-query';
import {
  fetchFinancialAnalytics,
  getFinancialOverview,
  getFinancialTrends,
  getMonthlyPnL,
  type AnalyticsDimension,
  type LedgerPaymentMethod,
  type OverviewFilters,
  type TrendGranularity,
  type TrendRange,
} from '../lib/financialsApi';

// Financial data is not real-time; a 5-minute stale window keeps the UI snappy
// while avoiding excessive refetching.
const FIVE_MINUTES = 5 * 60 * 1000;

// The firm multi-select (multi-company) is page-level state on FinancialsPage,
// so `companyIds` is passed explicitly instead of being threaded from the
// global switcher. Empty/undefined = all firms.

export function useFinancialOverview(filters: OverviewFilters = {}) {
  return useQuery({
    queryKey: ['financials', 'overview', filters],
    queryFn: () => getFinancialOverview(filters),
    staleTime: FIVE_MINUTES,
  });
}

export function useMonthlyPnL(
  year: number,
  month: number,
  method?: LedgerPaymentMethod,
  companyIds?: string[],
) {
  return useQuery({
    queryKey: ['financials', 'monthly', year, month, method ?? null, companyIds ?? []],
    queryFn: () => getMonthlyPnL(year, month, method, companyIds),
    staleTime: FIVE_MINUTES,
  });
}

export function useFinancialAnalytics(
  dimension: AnalyticsDimension,
  year?: number,
  month?: number,
  method?: LedgerPaymentMethod,
  companyIds?: string[],
) {
  return useQuery({
    queryKey: [
      'financials',
      'analytics',
      dimension,
      year ?? null,
      month ?? null,
      method ?? null,
      companyIds ?? [],
    ],
    queryFn: () => fetchFinancialAnalytics(dimension, year, month, method, companyIds),
    staleTime: FIVE_MINUTES,
  });
}

export function useFinancialTrends(
  range: TrendRange = '1y',
  granularity: TrendGranularity = 'month',
  method?: LedgerPaymentMethod,
  companyIds?: string[],
) {
  return useQuery({
    queryKey: ['financials', 'trends', range, granularity, method ?? null, companyIds ?? []],
    queryFn: () => getFinancialTrends(range, granularity, method, companyIds),
    staleTime: FIVE_MINUTES,
  });
}
